import { useState } from "react";

import { adminApi, loadSession, type AuditLogRow } from "../api";

/**
 * 🧾 Audit-Explorer (filters + signed-report export) and 🔍 Global-Search
 * combined on one page — both read the same append-only hash chain.
 */
export function AuditExplorer(): JSX.Element {
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");
  const [rows, setRows] = useState<AuditLogRow[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResult, setSearchResult] = useState<unknown>(null);
  const [error, setError] = useState<string | null>(null);

  async function runFilter(): Promise<void> {
    setError(null);
    try {
      setRows(
        await adminApi.get<AuditLogRow[]>("/api/v1/admin/audit", {
          action: action || undefined,
          actor: actor || undefined,
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function runSearch(): Promise<void> {
    setError(null);
    try {
      setSearchResult(await adminApi.get("/api/v1/admin/search", { q: searchQuery }));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  function exportSignedReport(): void {
    const session = loadSession();
    const base =
      (import.meta.env.VITE_ADMIN_API_BASE_URL as string | undefined) ?? "http://127.0.0.1:9443";
    fetch(`${base}/api/v1/admin/audit/export`, {
      credentials: "include",
      headers: session ? { "X-Admin-Session-Token": session.sessionToken } : {},
    })
      .then((res) => res.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "admin-audit-report.csv";
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }

  return (
    <div>
      <h2 className="page-title">🧾 Audit Explorer</h2>
      <p className="page-subtitle">
        Filtered view of the append-only, hash-chained audit log + signed CSV export.
      </p>
      {error && <div className="error-banner">{error}</div>}

      <div className="panel">
        <div className="form-row">
          <label htmlFor="action">Action</label>
          <input id="action" value={action} onChange={(e) => setAction(e.target.value)} />
          <label htmlFor="actor">Actor</label>
          <input id="actor" value={actor} onChange={(e) => setActor(e.target.value)} />
          <button onClick={runFilter}>Filter</button>
          <button onClick={exportSignedReport}>Export signed CSV</button>
        </div>
        <table>
          <thead>
            <tr>
              <th>Seq</th>
              <th>Actor</th>
              <th>Action</th>
              <th>Result</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.seq}</td>
                <td>{r.actor}</td>
                <td>{r.action}</td>
                <td>{r.result}</td>
                <td>{r.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <h3>🔍 Global Search</h3>
        <p className="page-subtitle">
          <code>sha256:&lt;checksum&gt;</code> → object → versions → copies → user → audit.
        </p>
        <div className="form-row">
          <input
            placeholder="sha256:… or object id"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ minWidth: 320 }}
          />
          <button onClick={runSearch}>Search</button>
        </div>
        {searchResult !== null && (
          <pre style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
            {JSON.stringify(searchResult, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}
